// Fonte ÚNICA da lista de rotas da navegação (gestão/cliente).
//
// Extraído VERBATIM dos computeds de `src/components/nav-bar.vue` (a nav antiga
// é a fonte de verdade). Dois consumidores:
//   1. nav-bar.vue           — chama buildRoutes(ctx) para montar defaultRoutes
//   2. navegacao-v2 (nova)   — consome ROUTE_DEFS por key para o esqueleto e
//                              formata onde precisa (routeName por contexto, upsell)
//
// Regras: ROUTE_DEFS é PURO (sem `this`); buildRoutes recebe tudo via `ctx`.
import { FEATURES } from '@constants/Text'
import { canShowOrUpsell, hasNoFeatureAccess } from '@utils/featureAccess'
import { podeVerIaDriven } from '@modules/global/utils/chat-ia-widget-visivel'
import { ROTA_IMERSIVA } from '@modules/aawz-intelligence-v2/views/chat-imersivo/constants'
import { canShowBackOfficeRoute } from '@modules/back-office/utils/permissions/permissions'
import { canShowFiniaRoute } from '@modules/finia/utils/permissions/permissions'
import { isConsultorBaseAtivos } from '@modules/settings/utils/base-ativos-consultor'
import { financeOptionsRoutes } from '@modules/finance/constants/Texts'
import { strategyOtionsRoutes } from '@modules/estrategia/constants/Texts'
import { treasuryOptionsRoutes } from '@modules/tesouraria/constants/Text'
import { rhOptionsRoutes } from '@modules/rh/constants/Text'
import { comissoesOptionsRoutes } from '@modules/comissoes/constants/Texts'
import { consolidadorOptionsRoutes } from '@modules/consolidator/constants/Text'
import { eventsOptionsRoutes } from '@modules/eventos/constants/Texts'
import { captableRoutes } from '@/src/modules/captable/constants/Text'
import { checkPermission as controladoriaV2CheckPermission } from '@modules/controladoria-v2/routes'

// Keys do mapa estático. Espelham o `name` usado na nav antiga; o Consolidador é
// dinâmico (overview vs. minha) e ganha duas keys + helper consolidadorRouteName.
export const ROUTE_KEYS = {
  DASHBOARD_BI: 'dashboard-bi',
  BI: 'bi',
  EMBED: 'embed',
  ANALISE_MERCADO: 'analise_mercado',
  CRM: 'crm',
  COMPLIANCE: 'compliance',
  FINIA: 'finia',
  FP_HOME: 'fp.home',
  REQUISICOES: 'requisicoes',
  SOLICITACOES: 'solicitacoes',
  FINANCE: 'finance',
  CAPTABLE: 'captable',
  VALUATION: 'valuation',
  COMISSOES: 'comissoes',
  BROKKR: 'brokkr',
  TREASURY: 'treasury',
  BILLING_SYSTEM: 'billing-system',
  DATA_ROOM: 'dataRoom',
  REPOSITORIO_ARQUIVOS: 'repositorio-arquivos',
  WS: 'ws',
  CLIENTES: 'clientes',
  SIMULADOR: 'simulador',
  CONSOLIDADOR: 'carteira',
  CONSOLIDADOR_MINHA: 'carteira.minha',
  BACK_OFFICE: 'back-office',
  ASSETS_MATURITY: 'assets-maturity',
  PROFESSIONALS: 'professionals',
  RH: 'rh',
  TAREFAS: 'tarefas',
  WORKFLOW_ENGINE: 'workflow-engine',
  AAWZ_DRIVE: 'aawz-drive',
  AAWZ_INTELLIGENCE: 'aawz-intelligence',
  // Visão imersiva do chat de IA. Module key PRÓPRIO (sem o prefixo
  // `aawz-intelligence.`) — com o prefixo, o dedup por module key da sidebar
  // 2026 descartaria este item em silêncio. Ver a rota em
  // `modules/aawz-intelligence/routes.js`.
  IA_IMERSIVO: ROTA_IMERSIVA,
  COMMERCIAL: 'commercial',
  BOLETADOR: 'boletador',
  WORKSPACE: 'workspace',
  OPPORTUNITIES: 'opportunities',
  TASKS: 'tasks',
  TASK: 'task',
  EVENTS: 'events',
  SETTINGS: 'settings',
  CENTRAL_CONFIG: 'central-config',
  FEE_FIXO: 'fee-fixo',
  PRODUTOS: 'produtos',
  DASHBOARD: 'dashboard',
  SERVICOS: 'servicos',
  MARKET_WINDOW: 'openMarketWindow',
  SUPORTE: 'suporte',
  AI_FRAMEWORK: 'ai-framework',
}

/**
 * ROUTE_DEFS — esqueleto ESTÁTICO por rota, copiado EXATAMENTE da nav antiga.
 * Cada entrada: { routeName, title, icon, flag }
 * - routeName: nome da rota (mesmo `name` da nav antiga).
 * - title: rótulo exibido (VERBATIM, inclui acentos e espaços do original).
 * - icon: nome do icon-svg / mdi (VERBATIM).
 * - flag: FEATURE que gateia o item (quando a nav antiga gateia por flag simples).
 *   Itens cuja visibilidade NÃO é flag simples (CRM upsell, Tesouraria por
 *   subrotas, FP sempre-visível, requisições por IS_INTERNAL) têm flag = null;
 *   a lógica fica em buildRoutes/ctx e, na nova nav, em helpers dedicados.
 *
 * PURO: nenhum `this`, nenhum acesso a store. Só dados.
 */
export const ROUTE_DEFS = {
  [ROUTE_KEYS.DASHBOARD_BI]: {
    routeName: 'dashboard-bi',
    title: 'BI',
    icon: 'mdi-view-dashboard',
    flag: FEATURES.BI_V2,
  },
  [ROUTE_KEYS.BI]: {
    routeName: 'bi',
    title: 'BI',
    icon: 'graph-pie',
    flag: FEATURES.BI_CUSTOMIZED,
  },
  [ROUTE_KEYS.EMBED]: {
    routeName: 'embed',
    title: 'BI Externo',
    icon: 'mdi-chart-box-plus-outline',
    flag: FEATURES.BI_EMBED,
  },
  [ROUTE_KEYS.ANALISE_MERCADO]: {
    routeName: 'analise_mercado',
    title: 'Análise de Mercado',
    icon: 'analyze-market',
    flag: FEATURES.CORRETORA,
  },
  // Vive no rodapé, adjacente a Configurações (grupo próprio — ver
  // `buildJornadaRoutes`), e NÃO dentro do submenu de Configurações: a spec-005 deu
  // estado POR PROFISSIONAL a cada etapa, e o grupo `settings` é `only_manager` para
  // todo mundo, salvo a exceção pontual da Base de Ativos.
  // Enquanto viveu lá dentro, a tela era inalcançável justamente para o dono do
  // progresso. Mesma posição conceitual da nav 2026 (`MODULE_TO_PARTE`), sem herdar
  // o gate de gestor.
  // `setting-outline` e não `route-square`: este último é o glifo da Parte
  // "Operação" na nav 2026 (`PARTES` em `navegacao-v2/constants/partes.js`), e a
  // Jornada aparece lá no rodapé de Configurações — dava o ícone de uma Parte a um
  // item de outra. O outline mantém a família do ícone de Configurações (`setting`,
  // cheio) sem se confundir com ele.
  [ROUTE_KEYS.CENTRAL_CONFIG]: {
    routeName: 'central-config',
    title: 'Jornada de Configuração',
    icon: 'setting-outline',
    flag: FEATURES.CENTRAL_CONFIG,
  },
  [ROUTE_KEYS.FEE_FIXO]: {
    routeName: 'fee-fixo',
    title: 'Migração Fee Fixo',
    icon: 'mdi-chart-timeline-variant',
    flag: FEATURES.FEE_FIXO,
  },
  [ROUTE_KEYS.CRM]: {
    routeName: 'crm',
    title: 'CRM',
    icon: 'crm',
    // Visibilidade é upsell (canShowOrUpsell): flag null aqui de propósito.
    flag: null,
  },
  [ROUTE_KEYS.COMPLIANCE]: {
    routeName: 'compliance',
    title: 'Compliance',
    icon: 'book-cover',
    // Visibilidade é upsell (canShowOrUpsell): flag null aqui de propósito.
    flag: null,
  },
  [ROUTE_KEYS.FINIA]: {
    routeName: 'finia',
    // Rótulo exibido é "Rotina Financeira" — a rota segue `finia`, nome da
    // aplicação externa embarcada (mesmo padrão de brokkr -> "Comissionamento").
    title: 'Rotina Financeira',
    // `wallet` em vez de `stars`: o sparkle é o ícone de IA (disclaimer-ia,
    // "Em breve") e, por ser um PNG embutido, ignora a prop `color` que a nav
    // passa — destoava do cinza/branco dos demais itens.
    icon: 'wallet',
    // Gateado pela funcionalidade de escritório "Plataforma BPO Financeiro":
    // sem a flag, o item nem aparece no menu (não é upsell).
    flag: FEATURES.PLATAFORMA_BPO_FINANCEIRO,
  },
  [ROUTE_KEYS.FP_HOME]: {
    routeName: 'fp.home',
    title: 'Financial Planning',
    icon: 'mdi-chart-timeline-variant',
    // Item sempre visível na nav antiga (can_show: true). Gating de upsell por
    // FINANCIAL_PLANNING_SIMULATOR / FINANCIAL_PLANNING fica em ctx/handlers.
    flag: null,
  },
  [ROUTE_KEYS.REQUISICOES]: {
    routeName: 'requisicoes',
    title: 'Requisições',
    icon: 'mdi-clipboard-list-outline',
    flag: FEATURES.REQUISICOES,
  },
  [ROUTE_KEYS.SOLICITACOES]: {
    routeName: 'solicitacoes',
    title: 'Solicitações',
    icon: 'clipboard-text',
    // Gateado pela feature flag SOLICITACOES (escritorio_funcionalidades) +
    // IS_INTERNAL durante o rollout (ver buildOperationalRoutes).
    flag: FEATURES.SOLICITACOES,
  },
  [ROUTE_KEYS.AI_FRAMEWORK]: {
    routeName: 'aiFramework',
    title: 'Central de IA',
    icon: 'ia-head',
    flag: FEATURES.AI_FRAMEWORK,
  },
  [ROUTE_KEYS.FINANCE]: {
    routeName: 'finance',
    title: 'Financeiro',
    icon: 'usd-circle',
    flag: FEATURES.BPO_FINANCE,
  },
  [ROUTE_KEYS.CAPTABLE]: {
    routeName: 'captable',
    title: 'Partnership',
    icon: 'receipt-items',
    flag: FEATURES.CAPTABLE,
  },
  [ROUTE_KEYS.VALUATION]: {
    routeName: 'valuation',
    title: 'Estratégia',
    icon: 'chart-grow',
    flag: FEATURES.ESTRATEGIA,
  },
  [ROUTE_KEYS.COMISSOES]: {
    routeName: 'comissoes',
    title: 'Comissões',
    icon: 'coin',
    flag: FEATURES.COMISSOES,
  },
  [ROUTE_KEYS.BROKKR]: {
    routeName: 'brokkr',
    title: 'Comissionamento',
    icon: 'mdi-sack-percent',
    flag: FEATURES.BROKKR,
  },
  [ROUTE_KEYS.TREASURY]: {
    routeName: 'treasury',
    title: 'Tesouraria',
    icon: 'mdi-hand-coin',
    // Visibilidade por subrotas (TREASURY ou FATURAMENTO ou REEMBOLSO): flag null.
    flag: null,
  },
  [ROUTE_KEYS.BILLING_SYSTEM]: {
    routeName: 'billing-system',
    title: 'Cobranças',
    icon: 'mdi-currency-usd',
    flag: FEATURES.COBRANCAS_CVM,
  },
  [ROUTE_KEYS.DATA_ROOM]: {
    routeName: 'dataRoom',
    title: 'Data room',
    icon: 'mdi-database',
    flag: FEATURES.PARTNERSHIP_DATA_ROOM,
  },
  [ROUTE_KEYS.REPOSITORIO_ARQUIVOS]: {
    routeName: 'repositorio-arquivos',
    title: 'Repositório de Arquivos',
    icon: 'mdi-package',
    flag: FEATURES.REPOSITORY_FILES,
  },
  [ROUTE_KEYS.WS]: {
    routeName: 'ws',
    title: 'Wealth Service',
    icon: 'hierarchy',
    flag: FEATURES.WS,
  },
  [ROUTE_KEYS.CLIENTES]: {
    routeName: 'clientes',
    title: 'Clientes',
    icon: 'profile-circular-frame',
    flag: FEATURES.CLIENTS,
  },
  [ROUTE_KEYS.SIMULADOR]: {
    routeName: 'simulador',
    title: 'Portfólio Review',
    icon: 'personalCard',
    flag: FEATURES.SIMULADOR,
  },
  // Consolidador — na nav antiga o routeName é dinâmico:
  //   gestão  → 'carteira' (Visão Geral) quando tem CONSOLIDATOR_OVERVIEW, senão 'carteira.minha'
  //   cliente → 'carteira.minha'
  // A flag de visibilidade é sempre ASSET_LOCATION. Use consolidadorRouteName(ctx)
  // para o destino. As duas keys carregam o mesmo skeleton estático.
  [ROUTE_KEYS.CONSOLIDADOR]: {
    routeName: 'carteira',
    title: 'Consolidador',
    icon: 'donut',
    flag: FEATURES.ASSET_LOCATION,
  },
  [ROUTE_KEYS.CONSOLIDADOR_MINHA]: {
    routeName: 'carteira.minha',
    title: 'Consolidador',
    icon: 'donut',
    flag: FEATURES.ASSET_LOCATION,
  },
  [ROUTE_KEYS.BACK_OFFICE]: {
    routeName: 'back-office',
    title: 'Back Office',
    icon: 'sheet',
    flag: FEATURES.BACK_OFFICE,
  },
  [ROUTE_KEYS.ASSETS_MATURITY]: {
    routeName: 'assets-maturity',
    title: 'Gerenciar Vencimentos',
    icon: 'calendar',
    flag: FEATURES.MANAGE_REDEMPTION,
  },
  [ROUTE_KEYS.PROFESSIONALS]: {
    routeName: 'professionals',
    title: 'Profissionais',
    icon: 'people-profile',
    flag: FEATURES.PROFESSIONALS,
  },
  [ROUTE_KEYS.RH]: {
    routeName: 'rh',
    title: 'Gestão de Desempenho ',
    icon: 'users',
    flag: FEATURES.RH,
  },
  [ROUTE_KEYS.TAREFAS]: {
    routeName: 'tarefas',
    title: 'Tarefas do dia',
    icon: 'calendar-check',
    flag: FEATURES.DAILY_TASKS,
  },
  [ROUTE_KEYS.WORKFLOW_ENGINE]: {
    routeName: 'workflow-engine.workflow-list',
    title: 'Automações',
    icon: 'mdi-sitemap-outline',
    flag: FEATURES.WORKFLOW_ENGINE,
  },
  [ROUTE_KEYS.AAWZ_DRIVE]: {
    routeName: 'aawz-drive',
    title: 'AAWZ Drive',
    icon: 'folder-cloud',
    // Visibilidade é upsell (flag OU !hideCrossSell): flag null aqui de propósito.
    flag: null,
  },
  [ROUTE_KEYS.IA_IMERSIVO]: {
    routeName: ROTA_IMERSIVA,
    title: 'Home IA',
    icon: 'home-ia',
    // Visibilidade não é flag simples: é `podeVerIaDriven` (a MESMA régua da
    // rota, do atalho e do guard da raiz), lida em `buildProfissionalRoutes`.
    flag: null,
  },
  [ROUTE_KEYS.AAWZ_INTELLIGENCE]: {
    routeName: 'aawz-intelligence',
    title: 'AAWZ Intelligence',
    icon: 'ia-imagem',
    // CHAT_IA (não IA_CRM_CHAT): a área não é mais uma aba do CRM, é um menu
    // próprio — gatear por uma flag com nome de CRM confundia (revisão do
    // Leandro no PR #1538).
    flag: FEATURES.CHAT_IA,
  },
  [ROUTE_KEYS.COMMERCIAL]: {
    routeName: 'commercial',
    title: 'Comercial',
    icon: 'chats',
    flag: FEATURES.IA_SDR,
  },
  [ROUTE_KEYS.BOLETADOR]: {
    routeName: 'boletador',
    title: 'Boletador',
    icon: 'mdi-ticket-percent',
    flag: FEATURES.BOLETADOR,
  },
  [ROUTE_KEYS.WORKSPACE]: {
    routeName: 'workspace',
    title: 'Workspace',
    icon: 'chats',
    flag: FEATURES.WORKSPACE,
  },
  [ROUTE_KEYS.OPPORTUNITIES]: {
    routeName: 'opportunities',
    title: 'Oportunidades',
    icon: 'mdi-chart-timeline-variant-shimmer',
    flag: FEATURES.OPPORTUNITIES,
  },
  [ROUTE_KEYS.TASKS]: {
    routeName: 'tasks',
    title: 'Tarefas',
    icon: 'mdi-calendar-outline',
    flag: FEATURES.TASKS,
  },
  [ROUTE_KEYS.TASK]: {
    routeName: 'task',
    title: 'Gerenciador de tarefas',
    icon: 'mdi-clipboard-check-outline',
    flag: FEATURES.TASK_V2,
  },
  [ROUTE_KEYS.EVENTS]: {
    routeName: 'events',
    title: 'Eventos',
    icon: 'calendar-check',
    flag: FEATURES.EVENTOS,
  },
  [ROUTE_KEYS.SETTINGS]: {
    // settings-v2 (/configuracoes) é a página atual; a antiga (/settings) está
    // obsoleta. A nav antiga já navegava p/ settings-v2 via handler goToSettings.
    routeName: 'settings-v2',
    title: 'Configurações',
    icon: 'setting',
    flag: null,
  },
  [ROUTE_KEYS.PRODUTOS]: {
    routeName: 'produtos',
    title: 'Educacional',
    icon: 'document-text',
    flag: FEATURES.PRODUCTS,
  },
  [ROUTE_KEYS.DASHBOARD]: {
    routeName: 'dashboard',
    title: 'Dashboard',
    icon: 'presentation-chart',
    flag: FEATURES.DASHBOARD,
  },
  [ROUTE_KEYS.SERVICOS]: {
    routeName: 'servicos',
    title: 'Serviços',
    icon: 'category',
    flag: FEATURES.SERVICES,
  },
  [ROUTE_KEYS.MARKET_WINDOW]: {
    routeName: 'openMarketWindow',
    title: 'Market News',
    icon: 'document',
    flag: FEATURES.NEWS,
  },
  [ROUTE_KEYS.SUPORTE]: {
    routeName: 'suporte',
    title: 'Suporte',
    icon: 'question',
    flag: FEATURES.SUPPORT,
  },
}

/**
 * Destino do Consolidador por contexto, VERBATIM da nav antiga.
 *   gestão  → 'carteira' se tem CONSOLIDATOR_OVERVIEW, senão 'carteira.minha'
 *   cliente → 'carteira.minha'
 * @param {Object} params
 * @param {Object} params.access store.getters['parameters/show']
 * @param {boolean} params.isCliente contexto cliente
 * @returns {string}
 */
export function consolidadorRouteName({ access, isCliente = false } = {}) {
  const show = access || {}
  if (isCliente) return ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].routeName
  return show[FEATURES.CONSOLIDATOR_OVERVIEW]
    ? ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR].routeName
    : ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].routeName
}

/**
 * Destino real do módulo Comissões, VERBATIM do handler goToComissoes da nav antiga:
 *   COMISSOES_OVERVIEW → 'comissoes.visao-geral', senão → 'comissoes.fechamentos'.
 * @param {Object} access store.getters['parameters/show']
 * @returns {string}
 */
export function comissoesRouteName(access) {
  const show = access || {}
  return show[FEATURES.COMISSOES_OVERVIEW]
    ? 'comissoes.visao-geral'
    : 'comissoes.fechamentos'
}

/**
 * Destino real do módulo Tesouraria, VERBATIM do handler goToTreasury da nav antiga.
 * Ordem de prioridade: TREASURY → FATURAMENTO (gestor/financeiro) → REEMBOLSO.
 * @param {Object} currentUser this.$currentUser()
 * @param {Object} access store.getters['parameters/show']
 * @returns {string} rota destino ('' se nenhuma se aplica)
 */
export function treasuryRouteName(currentUser, access) {
  const show = access || {}
  const user = currentUser || {}
  if (show[FEATURES.TREASURY]) return 'treasury.contas_a_pagar'
  if (show[FEATURES.FATURAMENTO] && (user.IS_GESTOR || user.IS_FINANCIAL)) {
    return 'treasury.contas_a_receber'
  }
  if (show[FEATURES.TESOURARIA_FORMULARIO_REEMBOLSO]) {
    return 'treasury.solicitations'
  }
  return ''
}

/**
 * Visibilidade do módulo Tesouraria, VERBATIM da nav antiga (showBySubroutes):
 * o item aparece se treasuryOptionsRoutes(...) retorna ≥1 subrota — ou seja, se
 * existe um destino real (TREASURY ou FATURAMENTO[gestor/fin] ou REEMBOLSO).
 * @param {Object} currentUser this.$currentUser()
 * @param {Object} access store.getters['parameters/show']
 * @returns {boolean}
 */
export function treasuryVisible(currentUser, access) {
  return treasuryOptionsRoutes(currentUser || {}, access || {}).length > 0
}

/**
 * Destino real das Configurações > Financeiro. Quando o usuário tem acesso à
 * Controladoria V2 (flag CONTROLADORIA_V2, ou interno AAWZ com acesso limitado),
 * o item leva à nova Controladoria; caso contrário, às configurações financeiras
 * antigas. Mesma regra do card "Financeiro" em settings/view-home-v2 — mantida
 * única aqui para o menu lateral e a página de Configurações não divergirem.
 * @param {Object} currentUser this.$currentUser()
 * @param {Object} access store.getters['parameters/show']
 * @returns {string}
 */
export function financeSettingsRouteName(currentUser, access) {
  const hasControladoriaV2 = controladoriaV2CheckPermission({
    user: currentUser || {},
    show: access || {},
  })
  return hasControladoriaV2
    ? 'controladoria-v2.fechamentos'
    : 'settings-v2.finance'
}

// Atalho interno: pega só os campos estáticos de uma key do ROUTE_DEFS.
function def(key) {
  const d = ROUTE_DEFS[key]
  return { name: d.routeName, title: d.title, icon: d.icon }
}

// Resolve as subrotas do BI customizado de forma LAZY: só o builder de Análise
// precisa delas, então o ctx entrega `getSubRoutesBI()` (thunk) ou um array
// direto `subRoutesBI`. Assim, builders que não usam BI (ex: Operacional) não
// disparam a leitura do store power-bi (que pode não existir em testes).
function resolveSubRoutesBI(ctx) {
  if (typeof ctx.getSubRoutesBI === 'function')
    return ctx.getSubRoutesBI() || []
  return ctx.subRoutesBI || []
}

/**
 * routesAnalise — VERBATIM do computed homônimo da nav antiga.
 */
export function buildAnaliseRoutes(ctx) {
  const access = ctx.access
  const routes = [
    {
      ...def(ROUTE_KEYS.DASHBOARD_BI),
      can_show: FEATURES.BI_V2,
    },
    {
      ...def(ROUTE_KEYS.BI),
      title:
        ROUTE_DEFS[ROUTE_KEYS.BI].title +
        (access[FEATURES.BI_V2] ? ' (legado)' : ''),
      can_show: FEATURES.BI_CUSTOMIZED,
      subRoutes: [...resolveSubRoutesBI(ctx)],
    },
    {
      ...def(ROUTE_KEYS.EMBED),
      can_show: FEATURES.BI_EMBED,
    },
    {
      ...def(ROUTE_KEYS.ANALISE_MERCADO),
      can_show: FEATURES.CORRETORA,
    },
    {
      ...def(ROUTE_KEYS.FEE_FIXO),
      can_show: FEATURES.FEE_FIXO,
      new: true,
    },
  ]

  return routes.filter((r) => r && access[r.can_show])
}

/**
 * routesOperational — VERBATIM do computed homônimo da nav antiga.
 */
export function buildOperationalRoutes(ctx) {
  const access = ctx.access
  const routes = [
    {
      ...def(ROUTE_KEYS.CRM),
      // can_show/noAccessIcon: ícone aparece se tem a flag do módulo ou, como upsell, se não tem OCULTAR_CROSS_SELL
      can_show: canShowOrUpsell(access, FEATURES.CRM),
      noAccessIcon: hasNoFeatureAccess(access, FEATURES.CRM),
    },
    {
      ...def(ROUTE_KEYS.COMPLIANCE),
      divider: false,
      can_show: canShowOrUpsell(access, FEATURES.COMPLIANCE),
      noAccessIcon: hasNoFeatureAccess(access, FEATURES.COMPLIANCE),
    },
    {
      ...def(ROUTE_KEYS.FP_HOME),
      action: ctx.handlers.goToFP,
      // Item sempre visível (entrada para a Calculadora PGBL livre).
      // O estilo acinzentado (noAccessIcon) só aparece como upsell quando o cross-sell
      // está visível (OCULTAR_CROSS_SELL=FALSE/NULL) e o escritório não tem flags FP —
      // sinalizando "contrate o módulo completo". Quando OCULTAR_CROSS_SELL=TRUE não há
      // intenção de upsell: o item aparece normal e leva direto à Calculadora.
      can_show: true,
      noAccessIcon:
        !ctx.hideCrossSell &&
        hasNoFeatureAccess(
          access,
          FEATURES.FINANCIAL_PLANNING_SIMULATOR,
          FEATURES.FINANCIAL_PLANNING
        ),
    },
    {
      ...def(ROUTE_KEYS.SOLICITACOES),
      // Gateado SÓ pela feature flag SOLICITACOES (escritorio_funcionalidades): mostra para QUALQUER
      // escritório com a funcionalidade. Quem é 2976 (AAWZ) cai na visão INTERNA (Operação AAWZ); os
      // demais (escritório cliente) caem na visão EXTERNA — o redirect (redirectSolicitacoesRoot) decide.
      can_show: FEATURES.SOLICITACOES,
      noAccessIcon: hasNoFeatureAccess(access, FEATURES.SOLICITACOES),
    },
    {
      ...def(ROUTE_KEYS.AI_FRAMEWORK),
      // Visível a QUALQUER profissional do escritório com a flag-mestra (gestor e
      // não-gestor acessam catálogo/meus-agentes — só a ativação/edição é gestor-only,
      // enforced no servidor e nos CTAs das telas — RF7.1/RF8.6, spec-002).
      can_show: FEATURES.AI_FRAMEWORK,
      noAccessIcon: hasNoFeatureAccess(access, FEATURES.AI_FRAMEWORK),
    },
  ]

  return routes.filter((r) => r && (access[r.can_show] || r.can_show === true))
}

/**
 * routesManagerOffice — VERBATIM do computed homônimo da nav antiga.
 */
export function buildManagerOfficeRoutes(ctx) {
  const access = ctx.access
  const currentUser = ctx.currentUser
  const routes = [
    {
      // Vem antes do Financeiro: são as duas faces do BPO financeiro, e a ordem
      // acompanha o fluxo de trabalho — a Rotina Financeira é o operacional
      // (contas a pagar/receber, posição), que alimenta o Financeiro, o
      // analítico (DRE/controladoria).
      ...def(ROUTE_KEYS.FINIA),
      // Gateado pela feature flag "Plataforma BPO Financeiro"
      // (escritorio_funcionalidades): sem ela o item é filtrado do menu.
      can_show: FEATURES.PLATAFORMA_BPO_FINANCEIRO,
      // Além da flag, é operação de retaguarda financeira: só gestor ou
      // profissional com role `financeiro`. O mesmo critério gateia a rota
      // (customPermission em @modules/finia/routes).
      onlyFinancialOrManager: true,
      new: true,
    },
    {
      ...def(ROUTE_KEYS.FINANCE),
      can_show: FEATURES.BPO_FINANCE,
      only_manager: false,
      subRoutes: [...financeOptionsRoutes(access, currentUser)],
    },
    {
      ...def(ROUTE_KEYS.CAPTABLE),
      can_show: FEATURES.CAPTABLE,
      subRoutes: [...captableRoutes(access, currentUser)],
    },
    {
      ...def(ROUTE_KEYS.VALUATION),
      can_show: FEATURES.ESTRATEGIA,
      subRoutes: [...strategyOtionsRoutes(currentUser, access)],
    },
    {
      ...def(ROUTE_KEYS.COMISSOES),
      action: ctx.handlers.goToComissoes,
      can_show: FEATURES.COMISSOES,
      subRoutes: [...comissoesOptionsRoutes(currentUser, access)],
    },
    {
      ...def(ROUTE_KEYS.BROKKR),
      can_show: FEATURES.BROKKR,
    },
    {
      ...def(ROUTE_KEYS.TREASURY),
      action: ctx.handlers.goToTreasury,
      showBySubroutes: true,
      subRoutes: [...treasuryOptionsRoutes(currentUser, access)],
    },
    {
      ...def(ROUTE_KEYS.BILLING_SYSTEM),
      can_show: FEATURES.COBRANCAS_CVM,
    },
    {
      ...def(ROUTE_KEYS.DATA_ROOM),
      can_show: FEATURES.PARTNERSHIP_DATA_ROOM,
    },
    {
      ...def(ROUTE_KEYS.REPOSITORIO_ARQUIVOS),
      can_show: FEATURES.REPOSITORY_FILES,
      only_manager: false,
    },
  ]
  return routes
    .filter((route) => {
      const hasCanShow =
        !route.hasOwnProperty('can_show') || access[route.can_show]
      return hasCanShow
    })
    .filter((route) => {
      const canShowBySubroute = !route.showBySubroutes || route.subRoutes.length
      return canShowBySubroute
    })
    .filter((route) => {
      return !(
        route.hasOwnProperty('only_manager') &&
        route.only_manager &&
        !currentUser.IS_GESTOR
      )
    })
    .filter((route) => {
      // Mesmo gate único que barra a rota (customPermission em
      // @modules/finia/routes): destino configurado + funcionalidade + perfil
      // financeiro (gestor OU role `financeiro`, mais amplo que only_manager,
      // que exigiria gestor e esconderia o módulo do financeiro). Chamar aqui a
      // mesma função — e não só o pedaço de perfil — é o que impede o item de
      // aparecer no menu apontando para uma rota que o guard vai negar.
      return (
        !route.onlyFinancialOrManager ||
        canShowFiniaRoute({ user: currentUser, access })
      )
    })
}

/**
 * routesManagerPortfolio — VERBATIM do computed homônimo da nav antiga.
 */
export function buildManagerPortfolioRoutes(ctx) {
  const access = ctx.access
  const currentUser = ctx.currentUser
  const routes = [
    {
      ...def(ROUTE_KEYS.WS),
      can_show: FEATURES.WS,
    },
    {
      ...def(ROUTE_KEYS.CLIENTES),
      divider: false,
      can_show: FEATURES.CLIENTS,
    },
    {
      ...def(ROUTE_KEYS.SIMULADOR),
      can_show: FEATURES.SIMULADOR,
    },
    {
      name: access[FEATURES.CONSOLIDATOR_OVERVIEW]
        ? ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR].routeName
        : ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].routeName,
      title: ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR].title,
      icon: ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR].icon,
      can_show: FEATURES.ASSET_LOCATION,
      subRoutes: [...consolidadorOptionsRoutes(currentUser, access)],
    },
    {
      ...def(ROUTE_KEYS.BACK_OFFICE),
      can_show: FEATURES.BACK_OFFICE,
      internalOrSelfService: true,
    },
    {
      ...def(ROUTE_KEYS.ASSETS_MATURITY),
      action: ctx.handlers.openAssetsMaturity,
      divider: true,
      can_show: FEATURES.MANAGE_REDEMPTION,
    },
  ]

  return routes.filter((r) => {
    if (r && r.onlyInternal) {
      return currentUser.IS_INTERNAL && access[r.can_show]
    }
    if (r && r.internalOrSelfService) {
      return canShowBackOfficeRoute({ user: currentUser, access: access })
    }
    return r && (access[r.can_show] || r.can_show === true)
  })
}

/**
 * routesManagerPeople — VERBATIM do computed homônimo da nav antiga.
 */
export function buildManagerPeopleRoutes(ctx) {
  const access = ctx.access
  const currentUser = ctx.currentUser
  const routes = [
    {
      ...def(ROUTE_KEYS.PROFESSIONALS),
      can_show: FEATURES.PROFESSIONALS,
      action: ctx.handlers.sendToGerenciarEquipe,
      only_manager: ctx.professionalsOnlyManager,
      divider: !access[FEATURES.PRODUCTS],
      actions: [
        {
          name: 'openProfissionais',
          title: 'Cadastrar Profissional',
          icon: 'mdi-account-tie-outline',
          action: ctx.handlers.openProfissionais,
        },
      ],
    },
    {
      ...def(ROUTE_KEYS.RH),
      can_show: FEATURES.RH,
      subRoutes: [...rhOptionsRoutes(currentUser, access)],
    },
    {
      ...def(ROUTE_KEYS.TAREFAS),
      can_show: FEATURES.DAILY_TASKS,
    },
  ]

  return routes.filter((r) => r && access[r.can_show])
}

/**
 * routesManagerTools — VERBATIM do computed homônimo da nav antiga.
 */
export function buildManagerToolsRoutes(ctx) {
  const access = ctx.access
  const currentUser = ctx.currentUser
  const routes = [
    {
      ...def(ROUTE_KEYS.AAWZ_DRIVE),
      // can_show: ícone aparece se o escritório tem FEATURES.AAWZ_DRIVE ou se não tem OCULTAR_CROSS_SELL (upsell)
      can_show: access[FEATURES.AAWZ_DRIVE] || !ctx.hideCrossSell,
      // noAccessIcon: exibe o ícone em estado acinzentado (upsell) quando a flag do módulo está ausente
      noAccessIcon: !access[FEATURES.AAWZ_DRIVE],
    },
    {
      ...def(ROUTE_KEYS.AAWZ_INTELLIGENCE),
      // can_show: ícone aparece se o escritório tem CHAT_IA ou se não tem
      // OCULTAR_CROSS_SELL (upsell) — mesmo padrão de CRM/AAWZ Drive.
      can_show: access[FEATURES.CHAT_IA] || !ctx.hideCrossSell,
      // noAccessIcon: exibe o ícone em estado acinzentado (upsell) quando a
      // flag do módulo está ausente.
      noAccessIcon: !access[FEATURES.CHAT_IA],
    },
    {
      ...def(ROUTE_KEYS.COMMERCIAL),
      can_show: FEATURES.IA_SDR,
    },
    {
      ...def(ROUTE_KEYS.BOLETADOR),
      can_show: FEATURES.BOLETADOR,
    },
    {
      ...def(ROUTE_KEYS.WORKFLOW_ENGINE),
      can_show: FEATURES.WORKFLOW_ENGINE,
    },
    {
      ...def(ROUTE_KEYS.WORKSPACE),
      whitelabel_only: !ctx.showWorkspace,
      can_show: FEATURES.WORKSPACE,
      divider: true,
      subRoutes: [
        {
          name: 'share_screen',
          title: 'Compartilhar tela atual',
          whitelabel_only: !ctx.showWorkspace,
          action: ctx.handlers.sendWorkspace,
        },
        {
          name: 'add_canal',
          title: 'Adicionar Canal',
          whitelabel_only: !ctx.showWorkspace,
          action: ctx.handlers.openCreateRoom,
        },
      ],
    },
    {
      ...def(ROUTE_KEYS.OPPORTUNITIES),
      can_show: FEATURES.OPPORTUNITIES,
    },
    {
      ...def(ROUTE_KEYS.TASKS),
      can_show: FEATURES.TASKS,
    },
    {
      ...def(ROUTE_KEYS.TASK),
      can_show: FEATURES.TASK_V2,
    },
    {
      ...def(ROUTE_KEYS.EVENTS),
      can_show: FEATURES.EVENTOS,
      subRoutes: [...eventsOptionsRoutes(access, currentUser)],
    },
    {
      ...def(ROUTE_KEYS.REQUISICOES),
      can_show: !!(currentUser.IS_INTERNAL || access[FEATURES.REQUISICOES]),
    },
  ]

  return routes.filter((r) => {
    const isFeatureEnabled = access[r.can_show] || r.can_show === true
    const isGestor = currentUser.IS_GESTOR

    if (r.hasOwnProperty('only_manager') && r.only_manager) {
      return isGestor && isFeatureEnabled
    }

    return isFeatureEnabled
  })
}

/**
 * routesJornada — grupo próprio da Jornada de Configuração, no rodapé.
 *
 * Fica adjacente a Configurações (mesma posição conceitual da nav 2026, ver
 * `MODULE_TO_PARTE`), mas em grupo separado e SEM `only_manager`: o grupo
 * `settings` é `only_manager` e filtrado para não-gestor em `buildRoutes`. Como a
 * spec-005 deu estado por profissional a cada etapa, herdar esse gate deixaria a
 * tela fora do alcance de quem executa a jornada.
 *
 * O gate por feature (`FEATURES.CENTRAL_CONFIG`) é o mesmo de sempre.
 */
export function buildJornadaRoutes(ctx) {
  const access = ctx.access
  const routes = [
    {
      ...def(ROUTE_KEYS.CENTRAL_CONFIG),
      can_show: FEATURES.CENTRAL_CONFIG,
    },
  ]

  return routes.filter((r) => r && access[r.can_show])
}

/**
 * Consultor (não-gestor) autorizado a operar a Base de Ativos do escritório.
 *
 * Delega para `isConsultorBaseAtivos` (@modules/settings/utils/base-ativos-consultor), que
 * EXCLUI o gestor de propósito — logo NÃO espelha `requireBaseAtivos`, que o inclui. O gestor
 * recebe o menu completo por outro caminho (`only_manager` mais abaixo). Menu mais frouxo que
 * o guard vira link para 404; mais apertado, vira funcionalidade inalcançável.
 *
 * Convive com `buildJornadaRoutes` acima: as duas afrouxam o gate gestor-only de
 * Configurações, por caminhos distintos. A Jornada saiu do grupo `settings` inteira;
 * a Base de Ativos permanece no grupo, que passa a ter `only_manager` condicional
 * (`buildSettingsRoutes` reduz o menu a Asset Allocation nesse caso).
 */
function canConsultorSeeBaseAtivos(ctx) {
  return isConsultorBaseAtivos(ctx.currentUser, ctx.access)
}

/**
 * routesSettings — VERBATIM do computed homônimo da nav antiga.
 *
 * Exceção: o consultor com `BASE_ATIVOS_CONSULTOR` recebe um menu reduzido a Asset
 * Allocation. O submenu de Configurações renderiza os subRoutes com `v-if="sr"` e NÃO lê
 * `can_show` — por isso o corte é feito no array, não numa prop (mesma armadilha
 * documentada no item da Jornada de Configuração logo abaixo).
 */
export function buildSettingsRoutes(ctx) {
  const handlers = ctx.handlers
  const currentUser = ctx.currentUser
  const access = ctx.access

  if (canConsultorSeeBaseAtivos(ctx)) {
    return [
      {
        ...def(ROUTE_KEYS.SETTINGS),
        action: handlers.openSettingsAssetAllocation,
        subRoutes: [
          {
            name: 'settings-v2.asset_allocation',
            title: 'Asset Allocation',
            action: handlers.openSettingsAssetAllocation,
            can_show: FEATURES.ASSET_LOCATION,
          },
        ],
      },
    ]
  }

  return [
    {
      ...def(ROUTE_KEYS.SETTINGS),
      action: handlers.goToSettings,
      subRoutes: [
        // A Jornada de Configuração saiu daqui e virou grupo próprio, logo acima deste
        // (`buildJornadaRoutes`). Este grupo é `only_manager` (salvo a exceção da Base de
        // Ativos, tratada no early return acima), e a spec-005 deu estado por profissional
        // a cada etapa — a tela ficava fora do alcance de quem executa a jornada. Ela
        // continua vizinha de Configurações no rodapé; o que mudou é que não herda mais o
        // gate de gestor. O gate por feature é o mesmo.
        //
        // ★ Nos itens abaixo o gate vai no ARRAY (`access[...] && {...}`), NÃO em
        // `can_show`: o submenu de Configurações renderiza os subRoutes com `v-if="sr"` e
        // não filtra por `can_show` — ali a prop é decorativa. Item sem acesso precisa
        // virar `false` aqui, senão aparece no menu e leva a um 404.
        {
          name: 'settings-v2.general',
          title: 'Configurações Gerais',
          action: handlers.openModuloGeneral,
        },
        {
          name: 'settings-v2.finance',
          title: 'Financeiro',
          action: handlers.openSettingsFinance,
          can_show: FEATURES.BPO_FINANCE,
        },
        {
          name: 'settings-v2.juridico',
          title: 'Partnership',
          action: handlers.openModuloJuridico,
          can_show: FEATURES.CAPTABLE,
        },
        {
          name: 'settings-v2.estrategia',
          title: 'Estratégia e Performance',
          action: handlers.openModuloEstrategia,
          can_show: FEATURES.ESTRATEGIA,
        },
        {
          name: 'settings-v2.crm',
          title: 'CRM',
          action: handlers.openSettingsCrm,
          can_show: FEATURES.CRM,
        },
        {
          name: 'settings-v2.bi',
          title: 'BI',
          action: handlers.openSettingsBi,
          can_show: FEATURES.BI_CUSTOMIZED,
        },
        {
          name: 'settings-v2.asset_allocation',
          title: 'Asset Allocation',
          action: handlers.openSettingsAssetAllocation,
          can_show: FEATURES.ASSET_LOCATION,
        },
        {
          name: 'settings-v2.financial-planning',
          title: 'Financial Planning',
          action: handlers.openSettingsFinancialPlanning,
          can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
        },
        ((!currentUser.IS_INTERNAL && access[FEATURES.COMISSOES]) ||
          (currentUser.IS_INTERNAL && access[FEATURES.COMISSOES_INTERNAL])) && {
          name: 'settings-v2.comissoes',
          title: 'Comissões',
          action: handlers.openSettingsComissoes,
          can_show: FEATURES.COMISSOES,
        },
        {
          name: 'settings-v2.rh',
          title: 'Gestão de desempenho',
          action: handlers.openSettingsRh,
          can_show: FEATURES.RH,
        },
        {
          name: 'settings-v2.compliance',
          title: 'Compliance',
          action: handlers.openSettingsCompliance,
          can_show: FEATURES.COMPLIANCE,
        },
      ],
    },
  ]
}

/**
 * routesManagerOuters — VERBATIM do computed homônimo da nav antiga.
 */
export function buildManagerOutersRoutes(ctx) {
  const access = ctx.access
  const currentUser = ctx.currentUser
  const routes = [
    {
      ...def(ROUTE_KEYS.PRODUTOS),
      can_show: FEATURES.PRODUCTS,
      divider: true,
    },
    {
      ...def(ROUTE_KEYS.DASHBOARD),
      can_show: FEATURES.DASHBOARD,
    },
    {
      ...def(ROUTE_KEYS.SERVICOS),
      can_show: FEATURES.SERVICES,
      divider: true,
      actions: [
        {
          name: 'openCadastrarServicos',
          title: 'Cadastrar Serviço',
          icon: 'mdi-briefcase-plus-outline',
          action: '',
        },
      ],
    },
    {
      ...def(ROUTE_KEYS.MARKET_WINDOW),
      second_icon: 'mdi-open-in-new',
      can_show: FEATURES.NEWS,
      action: ctx.handlers.openMarketWindow,
      validShow: true,
    },
    {
      ...def(ROUTE_KEYS.SUPORTE),
      second_icon: 'mdi-open-in-new',
      action: ctx.handlers.openSuporte,
      can_show: FEATURES.SUPPORT,
      validShow: true,
    },
  ]
  return routes.filter((r) => {
    if (!r) return false

    if (r.onlyInternal && !currentUser.IS_INTERNAL) {
      return false
    }

    return access[r.can_show]
  })
}

/**
 * Sub-rotas do Consolidador pro cliente final — mesma lista/regra de
 * `routesCliente` em `src/components/carteira-nav-bar.vue` (fonte das abas
 * BaseSegmentedTabs da própria página). Mantém sidebar e abas da página em
 * sincronia em vez de duas fontes de verdade divergindo. IPS só entra com
 * Compliance, mesmo gate de lá.
 */
function buildCarteiraClienteSubRoutes(access = {}) {
  const compliancePermission = !!access[FEATURES.COMPLIANCE]
  return [
    {
      name: ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].routeName,
      title: 'Minha carteira',
    },
    { name: 'carteira.movimentacoes', title: 'Extrato' },
    { name: 'carteira.evolucao', title: 'Evolução patrimonial' },
    { name: 'carteira.ips', title: 'IPS', noAccess: !compliancePermission },
    { name: 'carteira.analise', title: 'Análise portfólio' },
  ].filter((r) => !r.noAccess)
}

/**
 * Entrada do Financial Planning na navegação do CLIENTE final (RF-B01).
 *
 * É a MESMA entrada que o profissional tem — nome, ícone e destino saem de
 * `ROUTE_DEFS[ROUTE_KEYS.FP_HOME]`. O módulo se chama Financial Planning para
 * todo mundo; traduzir o nome só para o cliente criaria dois vocabulários para
 * a mesma coisa e quebraria o reconhecimento entre o que ele vê aqui e o que o
 * assessor cita numa conversa.
 *
 * É UM item de módulo, não um por tela: navegar entre as telas é papel das
 * abas do `NavBarNavigation`. Listar as três telas aqui as faria aparecer duas
 * vezes — na sidebar e nas abas — com a tela corrente acesa nos dois lugares.
 *
 * O que muda para o cliente NÃO é o menu, e sim o que existe atrás dele: as
 * outras rotas do módulo devolvem 404 no gate de perfil e por isso não entram
 * nesta lista. Oferecer no menu um caminho que termina em 404 é pior do que
 * não oferecer.
 */
const CLIENTE_FP_ROUTE = {
  name: ROUTE_DEFS[ROUTE_KEYS.FP_HOME].routeName,
  title: ROUTE_DEFS[ROUTE_KEYS.FP_HOME].title,
  icon: ROUTE_DEFS[ROUTE_KEYS.FP_HOME].icon,
}

/**
 * routesCliente — VERBATIM do computed homônimo da nav antiga (item único),
 * com `subRoutes` adicionado: sem isso, `adaptGestaoGroups` (nav 2026) não
 * sabe lidar com um item solto fora do wrapper `{title, routes:[...]}` e
 * descarta o item inteiro — sidebar do cliente final ficava vazia. `group` +
 * `hiddenTitle` seguem o mesmo padrão já usado por "Configurações" (linha
 * ~1017) — grupo sem título visível, só carregando as rotas.
 *
 * O Financial Planning entra como grupo ADICIONAL quando o escritório tem a
 * flag `FINANCIAL_PLANNING_CLIENTE` (RF-B02); o grupo do Consolidador segue
 * intacto em qualquer estado da flag (RF-B03). Cobrir os dois consumidores
 * (nav antiga e nav 2026) num ponto só é o motivo de a decisão morar aqui.
 */
export function buildClienteRoutes(ctx = {}) {
  const access = ctx.access || {}
  const groups = [
    {
      name: 'consolidador-cliente',
      title: '',
      hiddenTitle: true,
      group: true,
      routes: [
        {
          name: ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].routeName,
          title: ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].title,
          icon: ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR_MINHA].icon,
          subRoutes: buildCarteiraClienteSubRoutes(access),
        },
      ],
    },
  ]

  if (access[FEATURES.FINANCIAL_PLANNING_CLIENTE]) {
    groups.push({
      name: 'financial-planning-cliente',
      title: '',
      hiddenTitle: true,
      group: true,
      routes: [{ ...CLIENTE_FP_ROUTE }],
    })
  }

  return groups
}

// A Home comum (raiz da plataforma). Fora de `ROUTE_DEFS` porque não é módulo:
// não tem flag, upsell nem module key — é o item fixo do topo da sidebar.
const HOME_ROUTE_DEF = { routeName: 'home', title: 'Home', icon: 'house' }

// A Home da sidebar é UMA. Com a home por IA liberada (`podeVerIaDriven` — a
// MESMA régua do `customPermission` da rota, do atalho Ctrl/Cmd+I e do guard
// da raiz, que já manda `/` para `/home-ia`), a "Home IA" É a home do
// escritório e ocupa o lugar da Home comum, no mesmo slot; sem ela, a Home de
// sempre. Nunca as duas: emitidas como dois itens `group:false`, a nav 2026
// mostrava "Home" e "Home IA" empilhadas — a substituição era o comportamento
// desejado, não o bug (decisão do usuário, review #2254). Como a régua exige
// a nav 2026, a nav v1 continua vendo só a Home.
function buildHomeRoute(ctx) {
  const homeIa = podeVerIaDriven({ user: ctx.currentUser, access: ctx.access })
  const def = homeIa ? ROUTE_DEFS[ROUTE_KEYS.IA_IMERSIVO] : HOME_ROUTE_DEF
  return {
    name: def.routeName,
    title: def.title,
    icon: def.icon,
    group: false,
    validShow: true,
  }
}

/**
 * routesProfissional — VERBATIM do computed homônimo da nav antiga.
 * Monta os grupos a partir das seções acima.
 */
export function buildProfissionalRoutes(ctx) {
  const analise = buildAnaliseRoutes(ctx)
  const operational = buildOperationalRoutes(ctx)
  const managerOffice = buildManagerOfficeRoutes(ctx)
  const managerPortfolio = buildManagerPortfolioRoutes(ctx)
  const managerPeople = buildManagerPeopleRoutes(ctx)
  const managerTools = buildManagerToolsRoutes(ctx)
  const managerOuters = buildManagerOutersRoutes(ctx)
  const jornada = buildJornadaRoutes(ctx)
  const settings = buildSettingsRoutes(ctx)

  return [
    buildHomeRoute(ctx),
    {
      name: 'Análises',
      title: 'Análises',
      group: true,
      routes: [...analise],
      validShow: analise && analise.length,
    },
    {
      name: 'Gestão Operacional (dia-a-dia)',
      title: 'Gestão Operacional (dia-a-dia)',
      group: true,
      routes: [...operational],
      validShow: operational && operational.length,
    },
    {
      name: 'Gestão de escritórios',
      title: 'Gestão de escritórios',
      group: true,
      routes: [...managerOffice],
      validShow: managerOffice && managerOffice.length,
    },
    {
      name: 'Gestão de portfólios',
      title: 'Gestão de portfólios',
      group: true,
      routes: [...managerPortfolio],
      validShow: managerPortfolio && managerPortfolio.length,
    },
    {
      name: 'Gestão de pessoas',
      title: 'Gestão de pessoas',
      group: true,
      routes: [...managerPeople],
      validShow: managerPeople && managerPeople.length,
    },
    {
      name: 'Ferramentas',
      title: 'Ferramentas',
      group: true,
      routes: [...managerTools],
      validShow: managerTools && managerTools.length,
    },
    {
      name: 'Outros',
      title: 'Outros',
      group: true,
      routes: [...managerOuters],
      validShow: managerOuters && managerOuters.length,
    },
    // Grupo próprio, imediatamente acima de Configurações: mesma vizinhança visual,
    // sem o `only_manager` do grupo abaixo — ver `buildJornadaRoutes`.
    {
      name: 'jornada',
      title: 'Jornada de Configuração',
      hiddenTitle: true,
      group: true,
      validShow: jornada && jornada.length,
      routes: [...jornada],
    },
    {
      name: 'settings',
      title: 'Configurações',
      hiddenTitle: true,
      group: true,
      validShow: settings && settings.length,
      // Gestor-only, exceto para o consultor com `BASE_ATIVOS_CONSULTOR` — nesse caso
      // `buildSettingsRoutes` já reduziu o grupo a Asset Allocation, então liberá-lo aqui
      // não expõe nenhum outro item de Configurações.
      only_manager: !canConsultorSeeBaseAtivos(ctx),
      routes: [...settings],
    },
  ].filter((v) => v && v.validShow)
}

/**
 * buildRoutes(ctx) — saída final equivalente ao `defaultRoutes` da nav antiga.
 *
 * @param {Object} ctx
 * @param {Object} ctx.access            store.getters['parameters/show']
 * @param {Object} ctx.currentUser       this.$currentUser() (IS_INTERNAL, IS_GESTOR, ...)
 * @param {boolean} ctx.hideCrossSell    this.$hideCrossSell
 * @param {boolean} ctx.showWorkspace    this.$showWorkspace
 * @param {Function} [ctx.getSubRoutesBI] thunk lazy p/ subrotas do BI customizado
 * @param {Array}  [ctx.subRoutesBI]     alternativa: subrotas do BI já resolvidas
 * @param {boolean} ctx.professionalsOnlyManager  additionalPermissions.professionals()
 * @param {Object} ctx.handlers          métodos do componente (goToFP, goToComissoes, ...)
 * @returns {Array} grupos/rotas filtrados
 *
 * Cliente (IS_CLIENTE) vê apenas buildClienteRoutes() — VERBATIM da nav antiga.
 */
export function buildRoutes(ctx) {
  if (ctx.currentUser && ctx.currentUser.IS_CLIENTE) {
    return buildClienteRoutes(ctx)
  }

  const access = ctx.access
  const currentUser = ctx.currentUser

  let rotasProfissional = buildProfissionalRoutes(ctx)

  // Filtrar rotas profissional por permissao
  rotasProfissional = rotasProfissional
    .filter((route) => {
      return !route.hasOwnProperty('can_show') || access[route.can_show]
    })
    .filter((route) => {
      return !(
        route.hasOwnProperty('only_manager') &&
        route.only_manager &&
        !currentUser.IS_GESTOR
      )
    })

  // Filtrar actions por permissao
  rotasProfissional.forEach((route) => {
    if (route.actions) {
      route.actions = route.actions.filter((action) => {
        if (!action) {
          return false
        }
        return !action.hasOwnProperty('can_show') || access[action.can_show]
      })
    }
  })

  return rotasProfissional
}
